/**
 * Scripted event feeds for the theater demo page (dev-only). Each scenario is
 * a timed sequence of adapted TraceEvents — the same shape the run views
 * produce — so the demo exercises the exact seam the real mounts use,
 * including edge cases that are hard to reproduce on demand (parallel
 * fan-out, retries, HITL rounds, event storms).
 */

import type { TraceEvent } from '@evt/flow-stream-ui'

export interface ScenarioStep {
  /** Delay after the previous step at 1× speed (ms). */
  gapMs: number
  make: (base: { ts: string; atMs: number; seq: number }) => TraceEvent
}

export interface Scenario {
  id: string
  label: string
  steps: ScenarioStep[]
}

type Base = { ts: string; atMs: number; seq: number }

/** Plain Omit collapses discriminated unions to their common keys — distribute it. */
type DistributedOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never
type EventSpec = DistributedOmit<TraceEvent, 'ts' | 'atMs' | 'seq'>

const e =
  (partial: EventSpec) =>
  (base: Base): TraceEvent =>
    ({ ...base, ...partial }) as TraceEvent

const think = (text: string, lane?: string) =>
  e({
    kind: 'thinking',
    content: text,
    ...(lane ? { phase: 'subagent', agentId: lane } : {})
  })

const textEv = (text: string, lane?: string) =>
  e({
    kind: 'text',
    content: text,
    ...(lane ? { phase: 'subagent', agentId: lane } : {})
  })

const tool = (name: string, opts?: { server?: string; lane?: string; input?: Record<string, unknown> }) =>
  e({
    kind: 'tool_use',
    meta: { name, input: opts?.input ?? {}, ...(opts?.server ? { server: opts.server } : {}) },
    ...(opts?.lane ? { phase: 'subagent', agentId: opts.lane } : {})
  })

/** LIVE block-open marker — real engines emit this the moment a thinking/
    text/tool block STARTS; the finalized event follows only when it ends.
    The gaps between the two are the "long nothing" moments the theater must
    stay coherent through. */
const blockStart = (
  blockType: 'thinking' | 'text' | 'tool_use',
  opts?: { name?: string; server?: string; lane?: string }
) =>
  e({
    kind: 'block_start',
    meta: {
      blockType,
      ...(opts?.name ? { name: opts.name } : {}),
      ...(opts?.server ? { server: opts.server } : {})
    },
    ...(opts?.lane ? { phase: 'subagent', agentId: opts.lane } : {})
  })

/** The demo question — realistic request_human_input input so the journey +
    question card render the prompt/suggestions exactly like a real pause. */
export const DEMO_QUESTION_INPUT = {
  prompt: 'Should the solution be multi-tenant (one deployment shared by all subsidiaries) or single-tenant?',
  rationale: 'The brief mentions both subsidiaries and “their own environments”, which point in opposite directions.',
  suggestions: ['Multi-tenant, shared deployment', 'Single-tenant, one deployment per subsidiary'],
  attachmentMode: 'allowed'
} as const

const result = (opts?: { isError?: boolean; lane?: string }) =>
  e({
    kind: 'tool_result',
    meta: { output: {}, ...(opts?.isError ? { isError: true } : {}) },
    ...(opts?.lane ? { phase: 'subagent', agentId: opts.lane } : {})
  })

const preflight: ScenarioStep[] = [
  { gapMs: 0, make: e({ kind: 'request_received', meta: {} }) },
  { gapMs: 900, make: e({ kind: 'config_resolved', meta: {} }) },
  { gapMs: 1200, make: e({ kind: 'attachments_uploaded', meta: { count: 2 } }) },
  { gapMs: 2200, make: e({ kind: 'knowledge_resolved', meta: { count: 8, cacheHits: 6 } }) },
  { gapMs: 1000, make: e({ kind: 'turn_start' }) }
]

export const SCENARIOS: Scenario[] = [
  {
    id: 'full',
    label: 'Full run (tools, MCP, retry, specialists)',
    steps: [
      ...preflight,
      // Real emission order: the block ANNOUNCES itself, then goes silent for
      // its whole duration, then finalizes. The long gaps here are the
      // "interstitial" moments — the character must keep performing through
      // them (thinking at the desk, writing) instead of sitting blank.
      { gapMs: 800, make: blockStart('thinking') },
      { gapMs: 9500, make: think('Reading the brief…') },
      { gapMs: 700, make: blockStart('text') },
      { gapMs: 4200, make: e({ kind: 'text', content: 'Plan drafted.' }) },
      { gapMs: 1100, make: blockStart('tool_use', { name: 'code_execution' }) },
      { gapMs: 1500, make: tool('code_execution') },
      { gapMs: 5200, make: result() },
      { gapMs: 900, make: tool('code_execution') },
      { gapMs: 4200, make: result() },
      { gapMs: 800, make: blockStart('thinking') },
      { gapMs: 7800, make: think('The scaffold is ready; time to consult the docs service.') },
      { gapMs: 1200, make: blockStart('tool_use', { name: 'query_docs', server: 'context7' }) },
      { gapMs: 2200, make: tool('query_docs', { server: 'context7' }) },
      { gapMs: 4200, make: result({ isError: true }) },
      {
        gapMs: 1200,
        make: e({
          kind: 'engine_retry_attempt',
          meta: { attempt: 2, nextDelayMs: 800, errorCode: 'api_error', callStage: 'messages.create' }
        })
      },
      { gapMs: 2600, make: tool('query_docs', { server: 'context7' }) },
      { gapMs: 3600, make: result() },
      { gapMs: 1200, make: e({ kind: 'pause_turn_resumed' }) },
      { gapMs: 1000, make: e({ kind: 'cache_breakpoint', meta: { prefixTokens: 32_000 } }) },
      {
        gapMs: 1400,
        make: e({
          kind: 'turn_end',
          meta: { stopReason: 'tool_use', tokensIn: 1, tokensOut: 1, tokensCacheRead: 0, tokensCacheCreation: 0 }
        })
      },
      { gapMs: 900, make: e({ kind: 'turn_start' }) },
      { gapMs: 1200, make: tool('delegate_to_subagent') },
      {
        gapMs: 1600,
        make: e({
          kind: 'subagent_started',
          meta: { agentId: 'abap-architect', taskExcerpt: 'Design the object model' }
        })
      },
      {
        gapMs: 500,
        make: e({ kind: 'subagent_started', meta: { agentId: 'abap-coder', taskExcerpt: 'Implement the report' } })
      },
      {
        gapMs: 500,
        make: e({ kind: 'subagent_started', meta: { agentId: 'qa-reviewer', taskExcerpt: 'Prepare ABAP Unit' } })
      },
      { gapMs: 1200, make: blockStart('thinking', { lane: 'abap-architect' }) },
      { gapMs: 6200, make: think('Sketching entities…', 'abap-architect') },
      { gapMs: 600, make: tool('code_execution', { lane: 'abap-coder' }) },
      { gapMs: 900, make: think('Coverage matrix…', 'qa-reviewer') },
      { gapMs: 3800, make: result({ lane: 'abap-coder' }) },
      { gapMs: 800, make: textEv('Entity model finalized.', 'abap-architect') },
      {
        gapMs: 2200,
        make: e({ kind: 'subagent_finished', meta: { agentId: 'abap-architect', durationMs: 9000, status: 'success' } })
      },
      { gapMs: 1600, make: tool('code_execution', { lane: 'qa-reviewer' }) },
      { gapMs: 2600, make: result({ lane: 'qa-reviewer' }) },
      {
        gapMs: 1400,
        make: e({ kind: 'subagent_finished', meta: { agentId: 'abap-coder', durationMs: 12000, status: 'success' } })
      },
      {
        gapMs: 1800,
        make: e({
          kind: 'subagent_finished',
          meta: { agentId: 'qa-reviewer', durationMs: 13000, status: 'failure', failureCode: 'child_timeout' }
        })
      },
      { gapMs: 1600, make: think('Merging everything into the delivery.') },
      { gapMs: 3000, make: tool('code_execution') },
      { gapMs: 4200, make: result() },
      { gapMs: 1600, make: e({ kind: 'files_collected', meta: { count: 3 } }) },
      { gapMs: 2400, make: e({ kind: 'final', meta: { status: 'success', attachments: 3 } }) }
    ]
  },
  {
    id: 'hitl',
    label: 'Deep mode (asks you a question)',
    steps: [
      ...preflight,
      { gapMs: 900, make: think('The brief is ambiguous about the tenant model…') },
      { gapMs: 3600, make: tool('request_human_input', { input: { ...DEMO_QUESTION_INPUT } }) },
      {
        gapMs: 600,
        make: e({
          kind: 'awaiting',
          meta: {
            suspensionId: 'sus_demo1',
            toolName: 'request_human_input',
            expiresAt: new Date(Date.now() + 600_000).toISOString(),
            roundCount: 1
          }
        })
      },
      {
        gapMs: 9000,
        make: e({
          kind: 'user_reply',
          meta: { trigger: 'reply', round: 1, attachmentsIn: 0, replyPreview: 'B2B, multi-tenant' }
        })
      },
      { gapMs: 1200, make: e({ kind: 'turn_start' }) },
      { gapMs: 900, make: think('Multi-tenant it is.') },
      { gapMs: 3400, make: tool('code_execution') },
      { gapMs: 4600, make: result() },
      { gapMs: 1400, make: e({ kind: 'files_collected', meta: { count: 1 } }) },
      { gapMs: 2200, make: e({ kind: 'final', meta: { status: 'success', attachments: 1 } }) }
    ]
  },
  {
    id: 'paused',
    label: 'Paused (waiting at the window)',
    steps: [
      ...preflight,
      { gapMs: 900, make: think('Need to confirm the tenant model before going further…') },
      { gapMs: 3200, make: tool('request_human_input', { input: { ...DEMO_QUESTION_INPUT } }) },
      {
        gapMs: 600,
        make: e({
          kind: 'awaiting',
          meta: {
            suspensionId: 'sus_demo_hold',
            toolName: 'request_human_input',
            expiresAt: new Date(Date.now() + 600_000).toISOString(),
            roundCount: 1
          }
        })
      }
    ]
  },
  {
    id: 'memory',
    label: 'Memory + web lookups',
    steps: [
      ...preflight,
      { gapMs: 800, make: e({ kind: 'memory_op', meta: { stage: 'memory_recall_started' } }) },
      { gapMs: 2000, make: e({ kind: 'memory_op', meta: { stage: 'memory_recall_finished' } }) },
      { gapMs: 1200, make: think('I remember this contractor prefers concise runbooks.') },
      { gapMs: 3000, make: tool('web_search') },
      { gapMs: 3600, make: result() },
      { gapMs: 1200, make: e({ kind: 'text', content: 'Writing summary…' }) },
      { gapMs: 3000, make: e({ kind: 'files_collected', meta: { count: 1 } }) },
      { gapMs: 2200, make: e({ kind: 'final', meta: { status: 'partial_success', attachments: 1 } }) }
    ]
  },
  {
    id: 'failure',
    label: 'Failure (provider drops mid-run)',
    steps: [
      ...preflight,
      { gapMs: 900, make: think('Starting the analysis…') },
      { gapMs: 3400, make: tool('code_execution') },
      {
        gapMs: 3200,
        make: e({
          kind: 'engine_retry_attempt',
          meta: {
            attempt: 2,
            nextDelayMs: 1000,
            errorCode: 'engine_provider_stream_drop',
            callStage: 'messages.create'
          }
        })
      },
      {
        gapMs: 2600,
        make: e({
          kind: 'engine_retry_attempt',
          meta: {
            attempt: 3,
            nextDelayMs: 2000,
            errorCode: 'engine_provider_stream_drop',
            callStage: 'messages.create'
          }
        })
      },
      {
        gapMs: 3000,
        make: e({
          kind: 'error',
          meta: { code: 'engine_provider_stream_drop', message: 'SSE stream dropped', retryable: true }
        })
      }
    ]
  },
  {
    id: 'workshop',
    // The stage business that only shows up in the GAPS: one long tool block
    // with nothing on the wire is where crates get built and carried, where a
    // coffee gets fetched, and where the team gets briefed. Run this one at
    // 1× — speeding it up hides exactly what it is here to show.
    label: 'Workshop floor (production, standup, coffee)',
    steps: [
      ...preflight,
      { gapMs: 800, make: blockStart('thinking') },
      { gapMs: 6_000, make: think('Scoping the build…') },
      // A single long code_execution block: on the wire this is 90 seconds of
      // silence, on the floor it is crate after crate off the bench.
      { gapMs: 1_200, make: blockStart('tool_use', { name: 'code_execution' }) },
      { gapMs: 90_000, make: tool('code_execution') },
      { gapMs: 1_500, make: result() },
      // Delegation: door held, standup on the rug, brief handed over.
      { gapMs: 2_000, make: tool('delegate_to_subagent') },
      {
        gapMs: 2_500,
        make: e({ kind: 'subagent_started', meta: { agentId: 'abap-architect', taskExcerpt: 'Object model' } })
      },
      {
        gapMs: 400,
        make: e({ kind: 'subagent_started', meta: { agentId: 'abap-coder', taskExcerpt: 'Implementation' } })
      },
      {
        gapMs: 400,
        make: e({ kind: 'subagent_started', meta: { agentId: 'qa-reviewer', taskExcerpt: 'ABAP Unit' } })
      },
      // Specialists at the workbench too — the bench takes two pairs of hands.
      { gapMs: 6_000, make: tool('code_execution', { lane: 'abap-coder' }) },
      { gapMs: 1_000, make: tool('code_execution', { lane: 'qa-reviewer' }) },
      { gapMs: 45_000, make: result({ lane: 'abap-coder' }) },
      { gapMs: 2_000, make: result({ lane: 'qa-reviewer' }) },
      { gapMs: 3_000, make: think('Reviewing what came back…', 'abap-architect') },
      {
        gapMs: 4_000,
        make: e({ kind: 'subagent_finished', meta: { agentId: 'abap-coder', durationMs: 52_000, status: 'success' } })
      },
      {
        gapMs: 6_000,
        make: e({
          kind: 'subagent_finished',
          meta: { agentId: 'abap-architect', durationMs: 61_000, status: 'success' }
        })
      },
      {
        gapMs: 4_000,
        make: e({ kind: 'subagent_finished', meta: { agentId: 'qa-reviewer', durationMs: 64_000, status: 'success' } })
      },
      { gapMs: 3_000, make: tool('code_execution') },
      { gapMs: 20_000, make: result() },
      { gapMs: 2_000, make: e({ kind: 'files_collected', meta: { count: 4 } }) },
      { gapMs: 4_000, make: e({ kind: 'final', meta: { status: 'success', attachments: 4 } }) }
    ]
  },
  {
    id: 'storm',
    label: 'Event storm (compression stress)',
    steps: [
      ...preflight,
      ...Array.from({ length: 36 }, (_, i): ScenarioStep => {
        const pick = i % 6
        if (pick === 0) return { gapMs: 260, make: think(`burst ${i}`) }
        if (pick === 1) return { gapMs: 200, make: tool('code_execution') }
        if (pick === 2) return { gapMs: 220, make: result() }
        if (pick === 3) return { gapMs: 180, make: e({ kind: 'text', content: `chunk ${i}` }) }
        if (pick === 4) return { gapMs: 240, make: tool('bash_code_execution') }
        return { gapMs: 210, make: result() }
      }),
      { gapMs: 800, make: e({ kind: 'files_collected', meta: { count: 5 } }) },
      { gapMs: 1800, make: e({ kind: 'final', meta: { status: 'success', attachments: 5 } }) }
    ]
  }
]
